<script setup lang="ts">
import { type PropType, computed } from 'vue'
import DOMPurify from 'dompurify'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { getVueAssetImagePath } from '@/lib/utils'
import { checkIsShowMercariBrandPhase2 } from '@/lib/checkService'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import getRedirectSeoLink from '@/lib/getSeoRedirect'

const props = defineProps({
	title: {
		type: String,
		default: '',
	},
	items: {
		type: Array as PropType<
			{
				title: string
				description: string
			}[]
		>,
		required: true,
	},
	pricingText: {
		type: String,
		default: '',
	},
})

const PRICING_ICONS = ['delivery_v3', 'price_v3'] as const
const PRICING_LINK_PLACEHOLDER = '{pricingText}'

const isShowMercariBrandPhase2 = checkIsShowMercariBrandPhase2()
const pricingLink = `${getSiteBaseUrl()}/${getRedirectSeoLink('pricing')}/`
const formattedItems = computed(() => {
	return props.items.map((item, index) => {
		const icon = PRICING_ICONS[index]
		const description =
			index === 0
				? DOMPurify.sanitize(
						item.description.replace(
							PRICING_LINK_PLACEHOLDER,
							`<a class="text-o-500 underline" href="${pricingLink}" target="_blank">${props.pricingText}</a>`,
						),
						{ ADD_ATTR: ['target'] },
				  )
				: item.description

		return {
			...item,
			icon,
			description,
		}
	})
})
</script>

<template>
	<section class="bg-o-50" v-if="isShowMercariBrandPhase2">
		<div class="bs-container pt-distant-acquaintances">
			<div class="col-span-12">
				<h3 class="typography-h3-str mb-32px text-center text-n-950">
					{{ title }}
				</h3>
				<div
					class="mb-32px flex flex-col gap-16px mdp:mx-auto mdp:max-w-[700px] mdp:flex-row mdp:justify-center mdp:gap-32px"
				>
					<div
						class="flex-1 rounded-xl bg-white p-16px"
						v-for="(item, index) in formattedItems"
						:key="item.icon"
					>
						<div class="mb-8px flex">
							<BaseIcon
								:name="item.icon"
								class="mr-8px h-24px w-24px flex-shrink-0 fill-o-500"
							/>
							<h5 class="typography-h5-str text-n-800">{{ item.title }}</h5>
						</div>

						<div class="typography-b2-reg text-n-500">
							<p v-if="!index" v-html="item.description"></p>
							<p v-else>{{ item.description }}</p>
						</div>
					</div>
				</div>
				<div class="mx-auto max-w-[700px]">
					<picture>
						<source
							:srcset="getVueAssetImagePath('mercari-brand-globe.avif')"
							type="image/avif"
						/>
						<img
							class="w-full select-none object-contain object-center"
							:src="getVueAssetImagePath('mercari-brand-globe.png')"
							alt="globe banner"
							loading="lazy"
						/>
					</picture>
				</div>
			</div>
		</div>
	</section>
</template>
